/**
 * Post to the Candidates Store — lets a candidate publish (and edit) their
 * Store card so verified employers/recruiters can find them by skills,
 * availability, experience and salary fit.
 *
 * Mounted on: Studio (right rail, always visible), Dashboard (collapsible),
 * History (collapsible). Listens for the `resmo:publish-panel:open` window
 * event so toolbar CTAs can scroll to + flash the panel.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Globe, EyeOff, Copy, Download, Eye, Loader, ShieldCheck,
  Store, ChevronDown, ChevronUp, Pencil, HelpCircle, Star,
} from "lucide-react";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABILITY_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "actively_looking", label: "Actively looking" },
  { value: "open_to_offers", label: "Open to offers" },
  { value: "not_looking", label: "Not looking (just visible)" },
];
const REMOTE_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
  { value: "any", label: "Open to any" },
];

const EMPTY_FORM = {
  name_display: "", headline: "", location: "", years_experience: 0,
  skills: "", summary: "", show_email: false,
  availability: "", remote_mode: "",
  salary_min: "", salary_max: "", salary_currency: "USD",
  certifications: "", superpower: "",
};

const formFromPub = (p) => ({
  name_display: p.name_display || "",
  headline: p.headline || "",
  location: p.location || "",
  years_experience: p.years_experience || 0,
  skills: (p.skills || []).join(", "),
  summary: p.summary || "",
  show_email: !!p.show_email,
  availability: p.availability || "",
  remote_mode: p.remote_mode || "",
  salary_min: p.salary_min ?? "",
  salary_max: p.salary_max ?? "",
  salary_currency: p.salary_currency || "USD",
  certifications: (p.certifications || []).join(", "),
  superpower: p.superpower || "",
});

const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function StudioPublishPanel({ analysisId = null, collapsible = false }) {
  const [loading, setLoading] = useState(true);
  const [pub, setPub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(!collapsible);
  const [learnOpen, setLearnOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const rootRef = useRef(null);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/publish/mine");
      const rows = r.data.publications || [];
      const forThis = analysisId
        ? rows.find((p) => p.analysis_id === analysisId)
        : rows[0];
      setPub(forThis || null);
      if (forThis) setForm(formFromPub(forThis));
    } finally { setLoading(false); }
  }, [analysisId]);

  useEffect(() => { loadState(); }, [loadState]);

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setFlash(true);
      setTimeout(() => setFlash(false), 2200);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    };
    window.addEventListener("resmo:publish-panel:open", onOpen);
    return () => window.removeEventListener("resmo:publish-panel:open", onOpen);
  }, []);

  const isLive = pub && pub.status === "published";

  const buildFields = () => ({
    name_display: form.name_display.trim(),
    headline: form.headline.trim(),
    location: form.location.trim(),
    years_experience: Number(form.years_experience) || 0,
    skills: splitList(form.skills),
    summary: form.summary.trim(),
    show_email: form.show_email,
    availability: form.availability || null,
    remote_mode: form.remote_mode || null,
    salary_expectation_min: form.salary_min !== "" ? Number(form.salary_min) : null,
    salary_expectation_max: form.salary_max !== "" ? Number(form.salary_max) : null,
    salary_currency: form.salary_currency.trim() || null,
    certifications: splitList(form.certifications),
    superpower: form.superpower.trim() || null,
  });

  const submit = async () => {
    if (!form.name_display.trim()) {
      setError("Please enter your display name.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      if (isLive && editing) {
        await api.patch(`/publish/${pub.public_id}`, buildFields());
        setEditing(false);
      } else {
        await api.post("/publish/opt-in", { analysis_id: analysisId || null, ...buildFields() });
      }
      await loadState();
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!pub) return;
    if (!window.confirm("Remove your card from the Candidates Store? Employers won't see it until you republish.")) return;
    setBusy(true);
    try {
      await api.delete(`/publish/${pub.public_id}`);
      setEditing(false);
      await loadState();
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!pub?.public_url) return;
    try {
      await navigator.clipboard.writeText(pub.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard API blocked */ }
  };

  const showForm = !loading && (!isLive || editing);

  return (
    <section
      ref={rootRef}
      className="rounded-md p-4 md:p-5 mt-5"
      style={{
        background: "white",
        border: "2px solid #0F1B2D",
        boxShadow: flash ? "0 0 0 4px rgba(255,106,0,0.45), 4px 4px 0 0 #FFC145" : "4px 4px 0 0 #FFC145",
        transition: "box-shadow 0.3s ease",
      }}
      data-testid="studio-publish-panel"
    >
      <div
        className="flex items-start justify-between gap-3 flex-wrap"
        style={{ cursor: collapsible ? "pointer" : "default" }}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
        data-testid="studio-publish-header"
      >
        <div className="flex items-start gap-2">
          <Store size={18} style={{ color: "#FF6A00", marginTop: 2 }} />
          <div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#FF6A00", fontWeight: 700 }}>
              · Post to the Candidates Store
            </div>
            <div className="font-display uppercase leading-tight mt-0.5"
                 style={{ fontSize: "1.05rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}>
              Let employers find you.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill isLive={isLive} loading={loading} />
          {collapsible && (
            <span style={{ color: "#64748B" }} data-testid="studio-publish-chevron">
              {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3">
          <button
            onClick={() => setLearnOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest mb-2"
            style={{ color: "#1E40AF", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0 }}
            data-testid="publish-learn-more"
          >
            <HelpCircle size={12} /> What&apos;s the Candidates Store? {learnOpen ? "−" : "+"}
          </button>
          {learnOpen && (
            <div className="rounded p-3 mb-3 text-[12px] leading-relaxed"
                 style={{ background: "#EFF6FF", border: "1.5px solid #BFDBFE", color: "#1E3A5F" }}
                 data-testid="publish-learn-more-body">
              The Candidates Store is Resmo&apos;s talent directory — a place where <strong>verified
              employers and recruiters come to you</strong>. They search by skills, experience,
              availability and salary fit, and your card shows up in their results. No job
              hunting required: publish once, stay discoverable. Your email stays private
              unless you opt in — employers reach you through Resmo&apos;s contact relay, and you
              can unpublish anytime.
            </div>
          )}

          {loading ? (
            <div className="py-6 text-center font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#94A3B8" }}>· Checking status</div>
          ) : (
            <>
              {isLive && !editing && (
                <>
                  <FeaturedBoostCard pub={pub} />
                  <div className="rounded p-3 mb-3"
                       style={{ background: "#F8FAFC", border: "1.5px dashed #CBD5E1" }}>
                    <div className="flex items-start gap-3 flex-wrap">
                      <img src={`${BACKEND_URL}${pub.qr_url}`} alt="QR code"
                           style={{
                             width: 108, height: 108, background: "white",
                             border: "1.5px solid #0F1B2D", padding: 4, borderRadius: 4,
                           }}
                           data-testid="studio-publish-qr" />
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                             style={{ color: "#0E5A4A", fontWeight: 700 }}>
                          · You&apos;re live in the Store · Share this link or QR
                        </div>
                        <div className="text-[13px] font-mono-rm break-all mb-2" style={{ color: "#0F1B2D" }}
                             data-testid="studio-publish-link">
                          {pub.public_url}
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <button onClick={copyLink}
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                                  style={{ background: "#0F1B2D", color: "#FFC145", border: "1.5px solid #0F1B2D", fontWeight: 700 }}
                                  data-testid="studio-publish-copy">
                            <Copy size={11} /> {copied ? "Copied!" : "Copy link"}
                          </button>
                          <a href={`${BACKEND_URL}${pub.qr_url}`}
                             download={`resmo-${pub.public_id}-qr.png`}
                             className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                             style={{ background: "#FF6A00", color: "white", border: "1.5px solid #0F1B2D", fontWeight: 700, textDecoration: "none" }}
                             data-testid="studio-publish-qr-download">
                            <Download size={11} /> Download QR
                          </a>
                        </div>
                        <div className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest"
                             style={{ color: "#64748B", fontWeight: 600 }}
                             data-testid="studio-publish-views">
                          <Eye size={11} /> {pub.view_count || 0} employer views
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button onClick={() => { setEditing(true); setForm(formFromPub(pub)); }}
                            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                            style={{ background: "#0F1B2D", color: "white", border: "1.5px solid #0F1B2D", fontWeight: 700 }}
                            data-testid="studio-publish-edit">
                      <Pencil size={11} /> Edit details
                    </button>
                    <button onClick={unpublish} disabled={busy}
                            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                            style={{ background: "white", color: "#B91C1C", border: "1.5px solid #B91C1C", fontWeight: 700 }}
                            data-testid="studio-publish-unpublish">
                      <EyeOff size={11} /> Unpublish
                    </button>
                  </div>
                </>
              )}

              {showForm && (
                <div className="space-y-2">
                  {!isLive && (
                    <p className="text-[12px] leading-relaxed" style={{ color: "#334155" }}>
                      Publishing creates your Store card plus a public résumé link at{" "}
                      <span className="font-mono-rm" style={{ color: "#0F1B2D" }}>resmo.app/r/&lt;code&gt;</span>{" "}
                      with a downloadable QR. Only <strong>verified employers</strong> can see the
                      full page — you can unpublish anytime.
                    </p>
                  )}
                  <PublishField label="Display name" value={form.name_display}
                                onChange={(v) => setForm({ ...form, name_display: v })}
                                testid="studio-publish-name" required />
                  <PublishField label="Headline" value={form.headline}
                                onChange={(v) => setForm({ ...form, headline: v })}
                                placeholder="e.g. Senior Product Analyst"
                                testid="studio-publish-headline" />
                  <PublishField label="Your superpower — one-line pitch on your Store card"
                                value={form.superpower}
                                onChange={(v) => setForm({ ...form, superpower: v })}
                                placeholder="e.g. I turn messy data into decisions executives act on."
                                testid="studio-publish-superpower" />
                  <div className="grid grid-cols-2 gap-2">
                    <PublishField label="Location" value={form.location}
                                  onChange={(v) => setForm({ ...form, location: v })}
                                  placeholder="Denver, CO"
                                  testid="studio-publish-location" />
                    <PublishField label="Years of experience" value={form.years_experience}
                                  type="number"
                                  onChange={(v) => setForm({ ...form, years_experience: v })}
                                  testid="studio-publish-years" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <PublishSelect label="Availability" value={form.availability}
                                   options={AVAILABILITY_OPTIONS}
                                   onChange={(v) => setForm({ ...form, availability: v })}
                                   testid="studio-publish-availability" />
                    <PublishSelect label="Work mode" value={form.remote_mode}
                                   options={REMOTE_OPTIONS}
                                   onChange={(v) => setForm({ ...form, remote_mode: v })}
                                   testid="studio-publish-remote" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <PublishField label="Salary min" value={form.salary_min} type="number"
                                  onChange={(v) => setForm({ ...form, salary_min: v })}
                                  placeholder="90000"
                                  testid="studio-publish-salary-min" />
                    <PublishField label="Salary max" value={form.salary_max} type="number"
                                  onChange={(v) => setForm({ ...form, salary_max: v })}
                                  placeholder="130000"
                                  testid="studio-publish-salary-max" />
                    <PublishField label="Currency" value={form.salary_currency}
                                  onChange={(v) => setForm({ ...form, salary_currency: v })}
                                  placeholder="USD"
                                  testid="studio-publish-currency" />
                  </div>
                  <PublishField label="Top skills (comma-separated)" value={form.skills}
                                onChange={(v) => setForm({ ...form, skills: v })}
                                placeholder="SQL, Python, Dashboards"
                                testid="studio-publish-skills" />
                  <PublishField label="Certifications (comma-separated)" value={form.certifications}
                                onChange={(v) => setForm({ ...form, certifications: v })}
                                placeholder="PMP, AWS Solutions Architect"
                                testid="studio-publish-certifications" />
                  <PublishField label="Public summary (visible to employers)"
                                value={form.summary}
                                onChange={(v) => setForm({ ...form, summary: v })}
                                placeholder="What you do, who you help, and one signature win."
                                multiline testid="studio-publish-summary" />
                  <label className="flex items-center gap-2 text-[12px] mt-2 cursor-pointer" style={{ color: "#0F1B2D" }}>
                    <input type="checkbox" checked={form.show_email}
                           onChange={(e) => setForm({ ...form, show_email: e.target.checked })}
                           data-testid="studio-publish-show-email" />
                    <span>Show my email on the public page (otherwise employers use the Resmo relay)</span>
                  </label>
                  {error && (
                    <div className="text-[12px] font-mono-rm p-2 rounded"
                         style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", color: "#B91C1C" }}
                         data-testid="studio-publish-error">
                      {error}
                    </div>
                  )}
                  <div className="pt-2 flex justify-end gap-2">
                    {editing && (
                      <button onClick={() => { setEditing(false); setError(""); }}
                              className="inline-flex items-center gap-2 rounded-md px-3 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                              style={{ background: "white", color: "#64748B", border: "1.5px solid #CBD5E1", fontWeight: 700 }}
                              data-testid="studio-publish-cancel-edit">
                        Cancel
                      </button>
                    )}
                    <button onClick={submit} disabled={busy}
                            className="inline-flex items-center gap-2 rounded-md px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                            style={{
                              background: busy ? "#94A3B8" : "#FF6A00", color: "white",
                              border: "2px solid #0F1B2D",
                              boxShadow: "3px 3px 0 0 #0F1B2D",
                              fontWeight: 700, cursor: busy ? "wait" : "pointer",
                            }}
                            data-testid="studio-publish-submit">
                      {busy ? <Loader size={12} className="animate-spin" /> : <Globe size={12} />}
                      {busy ? "Saving…" : editing ? "Save changes" : "Post to the Store"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function StatusPill({ isLive, loading }) {
  if (loading) return null;
  return isLive ? (
    <span className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: "#D1FAE5", color: "#0E5A4A", fontWeight: 700, border: "1px solid #0E5A4A" }}
          data-testid="studio-publish-status-live">
      <ShieldCheck size={10} /> Live in Store
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: "#F1F5F9", color: "#64748B", fontWeight: 700, border: "1px solid #CBD5E1" }}
          data-testid="studio-publish-status-off">
      <EyeOff size={10} /> Not published
    </span>
  );
}

function FeaturedBoostCard({ pub }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const activeUntil =
    pub.featured && pub.featured_until && new Date(pub.featured_until) > new Date()
      ? new Date(pub.featured_until)
      : null;

  const buy = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/billing/checkout", {
        kind: "featured_boost",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Checkout failed — please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded p-3 mb-3"
         style={{ background: "#FFFBEB", border: "1.5px solid #F59E0B" }}
         data-testid="publish-featured-card">
      <div className="flex items-center gap-1.5 mb-1">
        <Star size={13} style={{ color: "#B45309", fill: activeUntil ? "#F59E0B" : "none" }} />
        <span className="font-mono-rm text-[10px] uppercase tracking-widest"
              style={{ color: "#B45309", fontWeight: 700 }}>
          {activeUntil ? "· Featured résumé — active" : "· Featured résumé"}
        </span>
      </div>
      {activeUntil ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[12px]" style={{ color: "#78350F" }} data-testid="publish-featured-until">
            Pinned near the top of employer searches until{" "}
            <strong>{activeUntil.toLocaleDateString()}</strong>.
          </div>
          <button onClick={buy} disabled={busy}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "white", color: "#B45309", border: "1.5px solid #B45309", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
                  data-testid="publish-featured-extend">
            {busy ? "Opening…" : "+ Extend 30 days · $9"}
          </button>
        </div>
      ) : (
        <>
          <div className="text-[12px] leading-relaxed mb-2" style={{ color: "#78350F" }}>
            Get seen first. Featured cards jump to the top of employer searches with a gold
            badge — <strong>$9 for 30 days</strong>.
          </div>
          <button onClick={buy} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "#F59E0B", color: "#0F1B2D", border: "2px solid #0F1B2D", boxShadow: "2px 2px 0 0 #0F1B2D", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
                  data-testid="publish-featured-buy">
            <Star size={11} /> {busy ? "Opening checkout…" : "Feature my résumé · $9"}
          </button>
        </>
      )}
      {err && (
        <div className="text-[11px] font-mono-rm mt-2 p-2 rounded"
             style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", color: "#B91C1C" }}
             data-testid="publish-featured-error">
          {err}
        </div>
      )}
    </div>
  );
}

function PublishField({ label, value, onChange, type = "text", placeholder, testid, required, multiline }) {
  return (
    <label className="block">
      <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-0.5"
           style={{ color: "#475569", fontWeight: 700 }}>
        · {label}{required && " *"}
      </div>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
                  placeholder={placeholder}
                  className="w-full rounded px-2 py-1.5 text-[12.5px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", resize: "vertical" }}
                  data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder}
               className="w-full rounded px-2 py-1.5 text-[12.5px]"
               style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
               data-testid={testid} />
      )}
    </label>
  );
}

function PublishSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-0.5"
           style={{ color: "#475569", fontWeight: 700 }}>
        · {label}
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full rounded px-2 py-1.5 text-[12.5px]"
              style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
              data-testid={testid}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
